import { WrapperWidget } from '@agentselly/ui';
import { AppLink } from '~/components/AppLink';
import { T } from '@tolgee/react';
import { Button } from '~/components/ui/Button';
import { Bath } from '~/generated/svg/Bath';
import { Floor } from '~/generated/svg/Floor';
import { Kitchen } from '~/generated/svg/Kitchen';
import { Masonry } from '~/generated/svg/Masonry';
import { Window } from '~/generated/svg/Window';

interface Props {
  readonly propertyConditions: Record<string, string>;
  readonly link: string;
}

const partComponents = {
  kitchen: Kitchen,
  bathrooms: Bath,
  windows: Window,
  flooring: Floor,
  masonry: Masonry,
};

const partOrder = [
  'bathrooms',
  'flooring',
  'kitchen',
  'masonry',
  'windows',
] as const;

const conditionScale = {
  renovation_needed: 0,
  well_maintained: 1,
  new_or_recently_renovated: 2,
};

const conditionColors = {
  renovation_needed: {
    light: 'bg-red-500',
    dark: 'dark:bg-red-400',
    textLight: 'text-red-500',
    textDark: 'dark:text-red-400',
  },
  well_maintained: {
    light: 'bg-amber-500',
    dark: 'dark:bg-amber-400',
    textLight: 'text-amber-500',
    textDark: 'dark:text-amber-400',
  },
  new_or_recently_renovated: {
    light: 'bg-green-500',
    dark: 'dark:bg-green-400',
    textLight: 'text-green-500',
    textDark: 'dark:text-green-400',
  },
};

export function PropertyConditionsWidget(props: Props) {
  return (
    <WrapperWidget>
      <div className="flex flex-col h-full gap-y-4">
        <h2 className="text-lg font-bold">
          <T>evaluation_property_conditions_widget_title</T>
        </h2>

        <p>
          <T>evaluation_property_condition_text</T>
        </p>

        <div className="flex flex-col gap-2">
          {partOrder
            .filter((part) => props.propertyConditions[`condition_${part}`])
            .map((part) => {
              const condition =
                props.propertyConditions[`condition_${part}`] || 'not_defined';
              const PartComponent = partComponents[part];
              const conditionValue =
                conditionScale[condition as keyof typeof conditionScale] ?? -1;

              return (
                <div
                  key={part}
                  className="grid grid-cols-[auto_minmax(0,1fr)_auto] items-center gap-3 p-3 rounded-lg border border-base-300 dark:border-transparent bg-base-100 dark:bg-base-800"
                >
                  <div className="flex items-center justify-center w-8 h-8 shrink-0 rounded-md bg-base-200 dark:bg-base-700 border border-base-300 dark:border-transparent">
                    {PartComponent && (
                      <PartComponent className="w-4 h-4 text-base-600 dark:text-base-200 [&_[fill='#333']]:fill-current [&_[stroke='#333']]:stroke-current" />
                    )}
                  </div>
                  <span className="text-sm font-medium min-w-0 truncate">
                    <T
                      keyName={`model_property_priceHubbleItem_${part}_name`}
                    />
                  </span>
                  <div className="flex items-center gap-2 shrink-0">
                    <div className="flex gap-1">
                      {[
                        0,
                        1,
                        2,
                      ].map((segment) => (
                        <div
                          key={segment}
                          className={`w-5 h-1.5 rounded-full ${
                            conditionValue === -1
                              ? 'bg-base-200 dark:bg-base-700'
                              : segment <= conditionValue
                              ? `${
                                  conditionColors[
                                    condition as keyof typeof conditionColors
                                  ]?.light
                                } ${
                                  conditionColors[
                                    condition as keyof typeof conditionColors
                                  ]?.dark
                                }`
                              : 'bg-base-200 dark:bg-base-700'
                          }`}
                        />
                      ))}
                    </div>
                    <span
                      className={`text-xs font-semibold min-w-[72px] text-right whitespace-nowrap ${
                        conditionValue === -1
                          ? 'text-base-500 dark:text-base-400'
                          : `${
                              conditionColors[
                                condition as keyof typeof conditionColors
                              ]?.textLight
                            } ${
                              conditionColors[
                                condition as keyof typeof conditionColors
                              ]?.textDark
                            }`
                      }`}
                    >
                      <T
                        keyName={
                          condition in conditionScale
                            ? `property_form_value_${condition}`
                            : `model_property_priceHubbleCondition_value_${condition}`
                        }
                      />
                    </span>
                  </div>
                </div>
              );
            })}
        </div>

        <AppLink
          to={props.link}
          className="self-start"
        >
          <Button
            primary
            showArrow
          >
            <T>ui_button_edit</T>
          </Button>
        </AppLink>
      </div>
    </WrapperWidget>
  );
}
